import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { LiquidacionService } from 'src/app/services/liquidacion/liquidacion.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { guardarArchivo } from 'src/app/utils/descargar';
import { etiquetaSinFirmante as etiquetaSF, rolesParams as rolesP, textoSinFirmante as textoSF } from 'src/app/utils/liquidacion-firmantes';
import { textoConteo, totalDeCortes, ventanaDelCorte } from 'src/app/utils/liquidacion-cortes';
import { baseDeCreditotal, cascadaDe } from './composicion-del-neto';
import { abrirDetalle, desgloseCierres, fmtBs, hayAsientos, mostrarCuadre, netoBs, tasaReal, textoPagado, usdTransferido } from './cifras-resumen';

/**
 * EL RESUMEN DEL DÍA (Mario, 12-oct; maqueta maqueta-resumen-del-dia.html aprobada; RSM-09 y 10). La primera pestaña de
 * Liquidación: «lo que te toca», el estado del día con su acta, las cuatro cifras (cada USD con su Bs y la tasa), el embudo,
 * por comercio, la tendencia de 30 días, los tiempos compactos, la composición del neto y las alertas. Todo lo calcula el
 * núcleo (GET /cierres/:dia/resumen); aquí solo se dibuja.
 */
@Component({
  selector: 'app-liquidacion-resumen',
  templateUrl: './liquidacion-resumen.component.html'
})
export class LiquidacionResumenComponent implements OnChanges {
  @Input() dia = '';
  /** Para «lo que te toca»: lleva a la pestaña de su trabajo. */
  @Output() ir = new EventEmitter<'cierre' | 'aprobar' | 'lotes' | 'resolver'>();

  r: any = null;
  cargando = false;
  error = '';
  porFirmar = 0;
  /** FIR-05 (Mario, 24-oct): las tandas que esperan un rol que nadie tiene (de `esperan_otra_firma` de la bandeja). */
  sinFirmante: any[] = [];
  rolDe = (k: string): string => ({ TES: 'Tesorero', GER: 'Gerente de finanzas', DIR: 'Director' } as any)[k] || k;
  /** FIR-09 (Mario, 25-oct): «Nadie tiene el rol» o quién lo tiene y por qué no firma; FIR-10: Roles filtrado. */
  textoSinFirmante = (e: any): string => textoSF(this.rolDe(e.falta), e.tanda?.n, e.con_rol_sin_poder);
  rolesParams = rolesP;
  tocaV: any = null; tendV: any = null; carrilesV: any = null; cascadaV: any[] = []; baseCreditotalV = 'de lo vendido';
  bajandoActa: 'pdf' | 'xlsx' | null = null;

  constructor(private liq: LiquidacionService, public _auth: AuthService) { }

  get puedeAprobar(): boolean { return this._auth.hasPermission('liquidacion.aprobar'); }
  get puedePreparar(): boolean { return this._auth.hasPermission('liquidacion.preparar'); }

  ngOnChanges(): void { this.cargar(); }

  cargar = async (): Promise<void> => {
    if (!this.dia) return;
    this.cargando = true; this.error = '';
    // La bandeja de firmas tarda más (calcula alertas): el Resumen se pinta apenas llega y «lo que te toca» se ajusta después.
    if (this.puedeAprobar) {
      this.liq.porAprobar().then((b) => { this.porFirmar = ((b && b.tandas) || []).length; this.sinFirmante = ((b && b.esperan_otra_firma) || []).filter((e: any) => e.firmantes_disponibles === 0); this.tocaV = this.toca; this.detalleAbierto = abrirDetalle(this.r, this.porFirmar); }).catch(() => { });
    }
    try {
      this.r = await this.liq.resumenDia(this.dia);
      // Se calculan una vez por carga (no en cada ciclo de la vista).
      this.tocaV = this.toca; this.detalleAbierto = abrirDetalle(this.r, this.porFirmar); this.tendV = this.tend; this.carrilesV = this.carriles; this.cascadaV = this.cascada; this.baseCreditotalV = baseDeCreditotal(this.r?.composicion);
    } catch (e: any) { this.r = null; this.error = e?.error?.msg || 'No se pudo armar el Resumen del día.'; }
    finally { this.cargando = false; }
  };

  /** 13.4: los cortes del día, tal como los manda el núcleo (n, code, estado, desde, hasta, usuario, liquidaciones, neto_usd). */
  get cortes(): any[] { return this.r?.cortes || []; }
  get totalCortes(): { liquidaciones: number; neto_usd: number } { return totalDeCortes(this.cortes); }
  ventana = ventanaDelCorte;
  /** COR-10: «2 comercios · 4 liquidaciones» (comercios distintos; las liquidaciones solo si son más). */
  conteo = textoConteo;

  // RSC-01 a 03: lo pagado, con el Bs del núcleo y la tasa real del lote.
  fmtBs = fmtBs; tasaReal = tasaReal; usdTransferido = usdTransferido; netoBs = netoBs; mostrarCuadre = mostrarCuadre; desgloseCierres = desgloseCierres;
  /** RSC-10: el «Detalle del día» está cerrado y solo se abre solo si hay algo que atender. */
  detalleAbierto = false;
  alternarDetalle = (e: Event): void => { this.detalleAbierto = (e.target as HTMLDetailsElement).open; };
  get tieneAsientos(): boolean { return hayAsientos(this.r?.pagado); }
  /** «Bs 1.234,00» de un monto USD (a la tasa del día); sin tasa, en USD. Para el texto pequeño de la tira. */
  txt = (usd: number): string => (this.bs(usd) !== null ? fmtBs(this.bs(usd)) : `$ ${(Number(usd) || 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

  // ── montos: todo USD con su Bs y la tasa (Mario, 12-oct) ──
  bs = (usd: number, tasa = this.r?.tasa): number | null => (tasa ? Math.round((Number(usd) || 0) * tasa * 100) / 100 : null);
  /** La cifra grande se ajusta a su tarjeta: con cientos de millones no se corta ni se parte. */
  tamano = (usd: number): number => { const n = Math.abs(Number(usd) || 0); return n >= 1e9 ? 18 : n >= 1e8 ? 20 : n >= 1e7 ? 22 : n >= 1e6 ? 24 : 28; };
  corto = (n: number): string => { const a = Math.abs(n); const f = (x: number) => x.toLocaleString('es-VE', { maximumFractionDigits: 1 }); return a >= 1e6 ? `${f(n / 1e6)} M` : a >= 1e3 ? `${f(n / 1e3)} mil` : f(n); };
  hora = (iso: any): string => (iso ? new Date(new Date(iso).getTime() - 4 * 3600e3).toISOString().slice(11, 16) : '—');
  fecha = (iso: string): string => { const d = new Date(`${iso}T12:00:00Z`); return `${['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'][d.getUTCDay()]} ${iso.slice(8, 10)}/${iso.slice(5, 7)}`; };
  min = (m: number | null): string => (m == null ? '—' : m < 60 ? `${Math.round(m)} min` : `${Math.floor(m / 60)} h${Math.round(m % 60) ? ' ' + Math.round(m % 60) + ' min' : ''}`);
  ancho = (v: number, total: number): number => (total > 0 ? Math.max(0, Math.min(100, (v / total) * 100)) : 0);

  // ── lo que te toca (RSM-10): aprobador con tandas por firmar → tesorero → gerente y director ──
  get toca(): { icono: string; t: string; d: string; b: string; ir: any; aviso?: boolean } | null {
    const r = this.r;
    if (!r) return null;
    const f = (n: number) => (this.bs(n) !== null ? `Bs ${(this.bs(n) as number).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : `$ ${(Number(n) || 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
    if (this.puedeAprobar && this.porFirmar > 0) {
      return { icono: 'fa-pen-nib', t: `${this.porFirmar} ${this.porFirmar === 1 ? 'tanda espera' : 'tandas esperan'} tu firma`, d: 'Cada una con su Excel de auditoría y el detalle por pagaré.', b: 'Ir a Por aprobar', ir: 'aprobar', aviso: true };
    }
    if (this.puedePreparar) {
      if (r.abierto) {
        return { icono: 'fa-clipboard-check', t: `Cierre del ${this.fecha(r.dia)} abierto · ${r.tandas_por_firmar} ${r.tandas_por_firmar === 1 ? 'tanda' : 'tandas'} por firmar`,
          d: `Aprobado ${f(r.aprobado.usd)} de ${f(r.vencia.usd)}. Al terminar, cierra el día.`, b: 'Ir a Cierre', ir: 'cierre', aviso: true };
      }
      // Con lotes abiertos, cargar respuestas; sin lotes abiertos, el resumen del día (decía «0 lotes siguen abiertos… carga
      // las respuestas que falten», visto en MT el 14-oct).
      if (r.lotes_abiertos > 0) {
        return { icono: 'fa-layer-group', t: `Cierre del ${this.fecha(r.dia)} cerrado · ${r.lotes_abiertos} ${r.lotes_abiertos === 1 ? 'lote sigue abierto' : 'lotes siguen abiertos'}`,
          d: `Pagado ${textoPagado(r)} de ${f(r.aprobado.usd)}; carga las respuestas que falten.`, b: 'Ir a Lotes', ir: 'lotes', aviso: true };
      }
    }
    if (!r.abierto && r.cerrado?.con_pendientes) {
      return { icono: 'fa-exclamation-triangle', t: `El día se cerró con ${f(r.cerrado.con_pendientes.usd)} sin liquidar · responsable: ${r.cerrado.por || 'quien cerró'}`,
        d: `${r.manana.comercios} ${r.manana.comercios === 1 ? 'comercio pasó' : 'comercios pasaron'} a mañana.${r.foto_congelada ? ' El acta del día está lista.' : ''}`, b: 'Ir a Cierre', ir: 'cierre' };
    }
    return r.abierto
      ? { icono: 'fa-hourglass-half', t: `Día abierto · aprobado ${f(r.aprobado.usd)} (${String(r.aprobado.pct_pagable).replace('.', ',')} % de lo pagable)`, d: 'El tesorero lo cierra al terminar.', b: 'Ir a Cierre', ir: 'cierre' }
      : { icono: 'fa-check', t: `Día cerrado · aprobado ${f(r.aprobado.usd)} · pagado ${textoPagado(r)}`, d: r.foto_congelada ? 'El acta del día está lista.' : 'Cerrado antes del acta.', b: 'Ir a Cierre', ir: 'cierre' };
  }

  // ── tendencia (SVG 900 × 220): barras de lo nuevo y lo pagado, línea del atraso acumulado ──
  readonly TW = 900; readonly TH = 220; readonly TP = 56;
  get tend(): any {
    const s = (this.r?.tendencia || []) as any[];
    // Con pocos días las barras no se estiran: cada día ocupa a lo sumo 60 px.
    const n = Math.max(s.length, 1), bw = Math.min(60, (this.TW - this.TP * 2) / n);
    const max = Math.max(1, ...s.map((x) => Math.max(x.nuevo, x.pagado, x.atraso)));
    const y = (v: number) => this.TH - this.TP - (v / max) * (this.TH - this.TP * 2);
    return {
      bw, guias: [0, 0.5, 1].map((t) => ({ y: y(max * t), et: this.corto(max * t) })),
      barras: s.map((x, i) => ({ ...x, x: this.TP + i * bw, yn: y(x.nuevo), hn: this.TH - this.TP - y(x.nuevo), yp: y(x.pagado), hp: this.TH - this.TP - y(x.pagado),
        cx: this.TP + i * bw + bw / 2, cy: y(x.atraso), et: `${x.dia.slice(8)}/${x.dia.slice(5, 7)}`, hoy: x.dia === this.dia })),
      linea: s.map((x, i) => `${this.TP + i * bw + bw / 2},${y(x.atraso)}`).join(' '),
    };
  }

  // ── tiempos compactos: las 5 fases y los carriles de tandas y lotes (SVG 1100 × 92) ──
  readonly ALERTA: Record<string, string> = { COMISION: 'Comisión distinta al contrato', MONTO_INUSUAL: 'Monto fuera de lo habitual', DUPLICADA: 'Venta duplicada', CUENTA_RECIENTE: 'Cuenta destino verificada hace poco' };
  readonly FASE: Record<string, string> = { PREPARACION: 'Preparación', FIRMAS: 'Firmas', PAGO_EN_BANCA: 'Pago en la banca', RESPUESTAS: 'Respuestas del banco', CIERRE: 'Cierre' };
  readonly CW = 1100; readonly CL = 70; readonly CR = 20;
  get carriles(): any {
    const t = this.r?.tiempos; if (!t) return null;
    const ev = [...t.carriles.tandas, ...t.carriles.lotes].map((e: any) => new Date(e.en).getTime()).sort((a, b) => a - b);
    if (!ev.length) return null;
    // Como en la maqueta: lo que pasa más de 14 h después (otro día) se fija al borde derecho; el eje es el del día.
    const t0 = ev[0], fuera = (ms: number) => ms - t0 > 14 * 3600e3;
    const t1 = Math.max(...ev.filter((m) => !fuera(m))) + 15 * 60e3;
    const ancho = this.CW - this.CL - this.CR - 30;
    const x = (ms: number) => (fuera(ms) ? this.CW - this.CR : this.CL + ((ms - t0) / Math.max(1, t1 - t0)) * ancho);
    const horas: any[] = [];
    const paso = Math.max(1, Math.ceil((t1 - t0) / 3600e3 / 10)) * 3600e3;   // a lo sumo ~10 marcas
    const h0 = new Date(t0); h0.setUTCMinutes(0, 0, 0);
    for (let h = h0.getTime() + 3600e3; h <= t1; h += paso) horas.push({ x: x(h), et: this.hora(h) });
    const punto = (e: any) => { const m = new Date(e.en).getTime(); return { ...e, x: x(m), titulo: `${fuera(m) ? this.fecha(new Date(m - 4 * 3600e3).toISOString().slice(0, 10)) + ' ' : ''}${this.hora(e.en)} · ${e.t}` }; };
    return { horas, tandas: t.carriles.tandas.map(punto), lotes: t.carriles.lotes.map(punto) };
  }

  // ── composición del neto (cascada): vendido/cobrado → comisiones → cargos → ajustes → neto (TXT-06) ──
  get cascada(): any[] { return cascadaDe(this.r?.composicion); }

  // ── el acta (ACT-09) ──
  bajarActa = async (formato: 'pdf' | 'xlsx'): Promise<void> => {
    this.bajandoActa = formato; this.error = '';
    try {
      const blob = await this.liq.actaDelDia(this.dia, formato);
      guardarArchivo(blob, `acta-cierre-${this.dia.replace(/-/g, '')}.${formato}`);
    } catch (e: any) {
      // con responseType blob, el mensaje del núcleo viene dentro de un Blob
      let msg = ''; try { msg = JSON.parse(await e?.error?.text()).msg; } catch (x) { }
      this.error = msg || 'No se pudo descargar el acta.';
    } finally { this.bajandoActa = null; }
  };
}
